import { useState } from "react";
import { api } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import { toast } from "sonner";
import { Heart, Sparkles } from "lucide-react";

const AMOUNTS = [25, 50, 100, 250, 500, 1000];

const inputCls = "w-full bg-background border border-input px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary";

export default function Donate() {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ name: "", email: "", employer: "", occupation: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const finalAmount = custom ? Number(custom) : amount;

  const submit = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount <= 0) {
      toast.error("Please enter a pledge amount.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/pledges", { ...form, amount: finalAmount });
      toast.success(`Thank you for pledging $${finalAmount}!`);
      setForm({ name: "", email: "", employer: "", occupation: "", message: "" });
      setAmount(100);
      setCustom("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-20 md:py-28">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="flag-rule"><span /><span /><span /></div>
            <span className="text-xs uppercase tracking-[0.22em] font-bold text-accent">Pledge Your Support</span>
          </div>
          <h1 className="font-serif text-5xl md:text-6xl font-black text-primary leading-[0.95] tracking-tight">
            Stand with Linda.
          </h1>
          <p className="mt-5 text-lg text-foreground/85 leading-relaxed">
            Your pledge fuels yard signs, mailers, and the grassroots work that keeps South Lebanon moving forward. Submit a pledge of intent below — our team will follow up with payment details.
          </p>
        </div>

        <div className="relative">
          <div className="absolute -inset-2 border-2 border-accent" aria-hidden />
          <form onSubmit={submit} className="relative bg-card border border-border p-8 md:p-12 space-y-7" data-testid="pledge-form">
            <div>
              <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-4">
                Pledge Amount <span className="text-accent">*</span>
              </span>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {AMOUNTS.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => { setAmount(a); setCustom(""); }}
                    data-testid={`amount-${a}`}
                    className={`py-3 font-serif font-bold text-lg border transition-colors ${
                      amount === a && !custom
                        ? "bg-accent text-accent-foreground border-accent"
                        : "bg-card text-primary border-border hover:border-accent"
                    }`}
                  >
                    ${a}
                  </button>
                ))}
              </div>
              <div className="mt-3">
                <input
                  type="number"
                  min="1"
                  placeholder="Or enter a custom amount"
                  className={inputCls}
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  data-testid="amount-custom"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <label>
                <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-2">Full Name <span className="text-accent">*</span></span>
                <input required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pledge-name" />
              </label>
              <label>
                <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-2">Email <span className="text-accent">*</span></span>
                <input type="email" required className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="pledge-email" />
              </label>
              <label>
                <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-2">Employer</span>
                <input className={inputCls} value={form.employer} onChange={(e) => setForm({ ...form, employer: e.target.value })} data-testid="pledge-employer" />
              </label>
              <label>
                <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-2">Occupation</span>
                <input className={inputCls} value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} data-testid="pledge-occupation" />
              </label>
            </div>

            <label className="block">
              <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-2">Note (optional)</span>
              <textarea rows="3" className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="pledge-message" />
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="pledge-submit"
              className="w-full bg-accent text-accent-foreground py-4 font-bold uppercase tracking-wider hover:bg-[#8A1333] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              <Heart size={18} /> {submitting ? "Submitting…" : `Pledge $${finalAmount || 0}`}
            </button>

            <p className="text-xs text-muted-foreground leading-relaxed">
              <Sparkles size={12} className="inline mr-1" />
              This is a pledge of intent. Our team will follow up with secure payment details and any required disclosure information. Contributions are not tax-deductible.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
